import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList,
  FilePlus,
  BarChart2,
  Brain,
  CheckCircle,
  FileText,
  Zap,
  Shield,
  Users,
  TrendingUp,
  Clock,
  Lightbulb,
  Target,
  Download,
} from "lucide-react";

const Divider = () => (
  <div className="w-full h-1 bg-gradient-to-r from-transparent via-[#FF4D00] to-transparent max-w-6xl mx-auto my-16 opacity-60" />
);

const FeaturesPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const mainFeatures = [
    {
      title: "Structured Assessments",
      icon: <ClipboardList className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Navigate structured COBIT 2019 and BCM assessments with clear sections, grouped questions, and a guided workflow.",
      details: [
        "Organized assessment areas",
        "Clear question grouping",
        "Logical assessment flow",
        "Context-aware structure",
      ],
      color: "from-blue-50 to-blue-100",
    },
    {
      title: "Evidence Upload",
      icon: <FilePlus className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Attach supporting documents and descriptions directly to assessment questions to strengthen response quality and review readiness.",
      details: [
        "Multi-file upload support",
        "Document management",
        "Evidence organization",
        "Review support",
      ],
      color: "from-green-50 to-green-100",
    },
    {
      title: "Auto-Generated Interim Reports",
      icon: <BarChart2 className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Receive interim reporting outputs immediately after completing an assessment, including visual summaries and key findings.",
      details: [
        "Instant report generation",
        "Visual dashboards",
        "Detailed analytics",
        "Interim reporting outputs",
      ],
      color: "from-purple-50 to-purple-100",
    },
    {
      title: "Capability and Readiness Scoring",
      icon: <Brain className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Automatically calculate capability levels for COBIT and readiness results for BCM based on your assessment responses.",
      details: [
        "Automated scoring",
        "Level calculations",
        "Gap identification",
        "Structured results",
      ],
      color: "from-yellow-50 to-yellow-100",
    },
    {
      title: "Assessment Progress Tracking",
      icon: <CheckCircle className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Track completion status clearly so you always know what has been completed and what still needs attention.",
      details: [
        "Real-time progress",
        "Visual indicators",
        "Completion rates",
        "Assessment visibility",
      ],
      color: "from-pink-50 to-pink-100",
    },
    {
      title: "PDF Export",
      icon: <FileText className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Export interim reporting outputs to PDF for internal review, stakeholder communication, and follow-up action planning.",
      details: [
        "Professional formatting",
        "Custom branding support",
        "Data visualization",
        "Multi-page export",
      ],
      color: "from-orange-50 to-orange-100",
    },
  ];

  const additionalFeatures = [
    {
      icon: <Shield className="w-10 h-10 text-[#FF4D00]" />,
      title: "Enterprise Security",
      description: "Role-based access control, secure data handling, and protection for assessment evidence and results.",
    },
    {
      icon: <Users className="w-10 h-10 text-[#FF4D00]" />,
      title: "Multi-User Collaboration",
      description: "Enable teams to contribute to assessments with clearer coordination and shared visibility.",
    },
    {
      icon: <TrendingUp className="w-10 h-10 text-[#FF4D00]" />,
      title: "Trend Analysis",
      description: "Track changes across multiple assessment cycles to measure improvement over time.",
    },
    {
      icon: <Clock className="w-10 h-10 text-[#FF4D00]" />,
      title: "Save and Resume",
      description: "Save progress and continue your assessment later without losing your work.",
    },
    {
      icon: <Lightbulb className="w-10 h-10 text-[#FF4D00]" />,
      title: "Actionable Insights",
      description: "Receive practical findings and improvement recommendations based on assessment responses.",
    },
    {
      icon: <Target className="w-10 h-10 text-[#FF4D00]" />,
      title: "Gap Analysis",
      description: "Identify the differences between current capability or readiness and desired assessment targets.",
    },
  ];

  const features = [
    {
      feature: "Pre-Built Assessment Frameworks",
      description: "Start immediately with COBIT 2019 and BCM assessment structures without manual setup",
    },
    {
      feature: "Evidence Upload",
      description: "Attach documents and files to support your assessment responses",
    },
    {
      feature: "Progress Tracking",
      description: "Monitor completion status in real time as your assessment progresses",
    },
    {
      feature: "Auto-Generated Interim Reports",
      description: "Generate interim reporting outputs with findings and recommendations",
    },
    {
      feature: "Mobile Access",
      description: "Access assessments on desktop, tablet, or mobile devices",
    },
    {
      feature: "Secure Dashboard",
      description: "View assessment history, results, and progress in a controlled environment",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-800 font-sans">
      <Helmet>
        <title>Features | Governance and Business Continuity Assessment Tool</title>
        <meta
          name="description"
          content="Explore features for COBIT 2019 and BCM assessments, including evidence upload, scoring, progress tracking, and interim reporting."
        />
      </Helmet>

      <section className="relative py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF4D00] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10" data-aos="fade-down">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            Powerful <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF4D00] to-orange-600">Assessment Features</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Everything you need to conduct structured governance and business continuity assessments with confidence.
          </p>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Core <span className="text-[#FF4D00]">Features</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainFeatures.map((feature, index) => (
              <div
                key={index}
                className={`relative p-8 rounded-2xl bg-gradient-to-br ${feature.color} border border-gray-200 hover:shadow-2xl hover:-translate-y-2 transition duration-300 group overflow-hidden`}
                data-aos="fade-up"
                data-aos-delay={index * 80}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition duration-300"></div>

                <div className="relative z-10">
                  <div className="mb-6 p-3 bg-white rounded-xl w-fit group-hover:bg-[#FF4D00]/10 transition">
                    {feature.icon}
                  </div>

                  <h3 className="text-2xl font-bold mb-3 text-gray-900">
                    {feature.title}
                  </h3>

                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {feature.description}
                  </p>

                  <div className="space-y-2">
                    {feature.details.map((detail, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                        <CheckCircle className="text-[#FF4D00] flex-shrink-0" size={16} />
                        {detail}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Additional <span className="text-[#FF4D00]">Capabilities</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalFeatures.map((feature, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-xl shadow hover:shadow-lg transition duration-300 border border-gray-200"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <div className="mb-4 p-3 bg-[#FF4D00]/10 rounded-lg w-fit">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">
                  {feature.title}
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            More <span className="text-[#FF4D00]">Features</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((item, idx) => (
              <div
                key={idx}
                className="p-8 rounded-xl bg-gradient-to-r from-[#FF4D00]/10 to-orange-600/10 border border-[#FF4D00]/20 hover:border-[#FF4D00] transition duration-300"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    <Zap className="text-[#FF4D00] flex-shrink-0" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {item.feature}
                    </h3>
                    <p className="text-gray-700">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6 bg-gradient-to-br from-[#FF4D00] to-orange-600 text-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Why These <span className="text-orange-100">Features Matter</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Save Time",
                description:
                  "Automated scoring and interim reporting reduce manual effort and help teams focus on improvement actions.",
                icon: "⏱️",
              },
              {
                title: "Improve Assessment Quality",
                description:
                  "Structured workflows and evidence support help teams produce clearer and more reliable assessment results.",
                icon: "✓",
              },
              {
                title: "Support Action",
                description:
                  "Recommendations and gap visibility help teams translate assessment findings into practical next steps.",
                icon: "→",
              },
            ].map((item, idx) => (
              <div key={idx} data-aos="zoom-in" data-aos-delay={idx * 100}>
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                <p className="text-orange-100 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Feature Use <span className="text-[#FF4D00]">Cases</span>
          </h2>

          <div className="space-y-8">
            {[
              {
                scenario: "Start Your Assessment",
                description:
                  "Begin your COBIT 2019 or BCM assessment by answering structured questions designed to evaluate current governance or continuity capability.",
                features: ["Structured Questions", "Guided Assessment", "Progress Tracking"],
              },
              {
                scenario: "Upload Evidence",
                description:
                  "Attach supporting documents and files to build a stronger, evidence-supported record of your responses.",
                features: ["File Upload", "Document Storage", "Evidence Support"],
              },
              {
                scenario: "View Your Results",
                description:
                  "Review capability or readiness results and identify recommended actions for governance and continuity improvement.",
                features: ["Scoring Results", "Recommendations", "Visual Summaries"],
              },
              {
                scenario: "Export and Share",
                description:
                  "Download an interim report in PDF format for internal review, stakeholder communication, or planning discussions.",
                features: ["PDF Export", "Interim Report", "Ready to Share"],
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="p-8 border-l-4 border-[#FF4D00] bg-gray-50 rounded-r-xl hover:shadow-lg transition duration-300"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {item.scenario}
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {item.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {item.features.map((feature, fidx) => (
                    <span
                      key={fidx}
                      className="inline-block px-4 py-2 bg-[#FF4D00]/20 text-[#FF4D00] rounded-full text-sm font-semibold"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto text-center" data-aos="fade-up">
          <h2 className="text-4xl font-bold mb-6">
            Experience These <span className="text-[#FF4D00]">Features Today</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Start your governance and business continuity assessment with a structured, evidence-supported feature set.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/login")}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
            >
              <Zap size={20} />
              Start Assessment
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#FF4D00] text-[#FF4D00] rounded-lg font-semibold hover:bg-[#FF4D00]/5 transition"
            >
              <Download size={20} />
              Request Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;